import React, { useState, useEffect } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { Mail, Calendar, UserCheck, X, Plus, Edit2, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface FreeAccessGrant {
  id: string;
  email: string;
  reason?: string;
  expires_at?: string;
  is_active: boolean;
  granted_by?: string;
  created_at: string;
  updated_at: string;
}

const SubscriptionFreeAccessSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const [grants, setGrants] = useState<FreeAccessGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGrant, setEditingGrant] = useState<FreeAccessGrant | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  
  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchGrants = async () => {
    if (!api) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/admin/subscription-free-access?active_only=${activeOnly}`);
      setGrants(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load free access grants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrants();
  }, [api, activeOnly]);

  const handleCreate = async () => {
    if (!api) return;
    
    try {
      setSubmitting(true);
      setFormError(null);
      
      const payload: any = {
        email: formEmail.trim(),
        reason: formReason.trim() || undefined,
        expires_at: formExpiresAt ? new Date(formExpiresAt).toISOString() : undefined
      };
      
      await api.post('/admin/subscription-free-access', payload);
      
      // Reset form and refresh list
      setShowCreateModal(false);
      setFormEmail('');
      setFormReason('');
      setFormExpiresAt('');
      await fetchGrants();
    } catch (err: any) {
      setFormError(err.response?.detail || err.message || 'Failed to create free access grant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!api || !editingGrant) return;
    
    try {
      setSubmitting(true);
      setFormError(null);
      
      const payload: any = {
        reason: formReason.trim() || undefined,
        expires_at: formExpiresAt ? new Date(formExpiresAt).toISOString() : undefined
      };
      
      await api.patch(`/admin/subscription-free-access/${editingGrant.id}`, payload);
      
      // Reset form and refresh list
      setEditingGrant(null);
      setFormReason('');
      setFormExpiresAt('');
      await fetchGrants();
    } catch (err: any) {
      setFormError(err.response?.detail || err.message || 'Failed to update free access grant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (grant: FreeAccessGrant) => {
    if (!api) return;
    
    if (!window.confirm(`Are you sure you want to revoke free access for ${grant.email}?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/subscription-free-access/${grant.id}`);
      await fetchGrants();
    } catch (err: any) {
      window.alert(err.response?.detail || err.message || 'Failed to revoke free access');
    }
  };

  const openEditModal = (grant: FreeAccessGrant) => {
    setEditingGrant(grant);
    setFormReason(grant.reason || '');
    setFormExpiresAt(grant.expires_at ? new Date(grant.expires_at).toISOString().split('T')[0] : '');
    setFormError(null);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingGrant(null);
    setFormEmail('');
    setFormReason('');
    setFormExpiresAt('');
    setFormError(null);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">
          Subscription Free Access
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Grant users access to the platform without requiring a subscription
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="rounded border-neutral-300 dark:border-neutral-600"
            />
            Active only
          </label>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-black rounded-lg hover:bg-brand-hover transition-colors"
        >
          <Plus size={16} />
          Grant Free Access
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      )}

      {/* Grants list */}
      {!loading && grants.length === 0 && (
        <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
          {activeOnly ? 'No active free access grants' : 'No free access grants found'}
        </div>
      )}

      {!loading && grants.length > 0 && (
        <div className="space-y-3">
          {grants.map((grant) => (
            <div
              key={grant.id}
              className="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail size={16} className="text-neutral-500" />
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {grant.email}
                    </span>
                    {!grant.is_active && (
                      <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
                        Revoked
                      </span>
                    )}
                  </div>
                  
                  {grant.reason && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                      Reason: {grant.reason}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-xs text-neutral-500 dark:text-neutral-400">
                    <div className="flex items-center gap-1">
                      <UserCheck size={12} />
                      Granted {format(new Date(grant.created_at), 'MMM d, yyyy')}
                    </div>
                    
                    {grant.expires_at && (
                      <div className="flex items-center gap-1">
                        <Calendar size={12} />
                        Expires {format(new Date(grant.expires_at), 'MMM d, yyyy')}
                      </div>
                    )}
                    
                    {!grant.expires_at && (
                      <div className="flex items-center gap-1">
                        <Calendar size={12} />
                        No expiration
                      </div>
                    )}
                  </div>
                </div>
                
                {grant.is_active && (
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => openEditModal(grant)}
                      className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleRevoke(grant)}
                      className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      title="Revoke"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingGrant) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {editingGrant ? 'Edit Free Access' : 'Grant Free Access'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              >
                <X size={20} />
              </button>
            </div>
            
            {formError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
                {formError}
              </div>
            )}
            
            <div className="space-y-4">
              {!editingGrant && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder="user@example.com"
                    required
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Reason
                </label>
                <input
                  type="text"
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  placeholder="Beta tester, Partner, etc."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={formExpiresAt}
                  onChange={(e) => setFormExpiresAt(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Leave empty for permanent access
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={editingGrant ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-brand-primary text-black rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
                disabled={submitting || (!editingGrant && !formEmail.trim())}
              >
                {submitting ? 'Saving...' : editingGrant ? 'Update' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionFreeAccessSection; 